import { Sparkles, GitFork } from "lucide-react";
import { Pill, NarrativeCard, EmptyState, Button } from "@/design/components";
import { useSeed, useMoney } from "@/domain/hooks";
import { useTrace } from "@/shell/TraceProvider";
import { unitMetrics, generateInsights, type Insight } from "@/domain/insights";
import { formatPct } from "@/design/format";

const INSIGHT_VARIANT: Record<Insight["kind"], "danger" | "warn" | "success"> = {
  outlier: "danger",
  watch: "warn",
  positive: "success",
};
const INSIGHT_LABEL: Record<Insight["kind"], string> = {
  outlier: "Outlier",
  watch: "Watch",
  positive: "Healthy",
};

// Each site reads as its own comparison series; colour comes from the viz palette
// (a distinct, non-semantic hue per row), never an arbitrary green/red.
const VIZ_SERIES = ["bg-viz1", "bg-viz2", "bg-viz3", "bg-viz4", "bg-viz5", "bg-viz6"];

export function ComparisonView() {
  const seed = useSeed();
  const money = useMoney();
  const { openTrace } = useTrace();
  const rows = unitMetrics(seed);
  const insights = generateInsights(seed);

  return (
    <div className="space-y-6">
      {/* Insight engine */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <span className="inline-flex h-[22px] items-center gap-1 rounded-full bg-brandSoft px-2 text-meta text-brand">
            <Sparkles className="h-3.5 w-3.5" /> Insight engine
          </span>
          <h2 className="text-sm font-semibold text-ink">What the data is telling you</h2>
        </div>
        {insights.length === 0 ? (
          <EmptyState title="No standout signals" body="The sites are tracking closely together; no metric is an outlier this period." />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {insights.map((i) => (
              <div key={i.id} className="rounded-card border border-line bg-card p-4">
                <Pill variant={INSIGHT_VARIANT[i.kind]}>{INSIGHT_LABEL[i.kind]}</Pill>
                <p className="mt-2 text-sm font-semibold text-ink">{i.title}</p>
                <p className="mt-1 text-sm leading-snug text-mute">{i.body}</p>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-xs text-faint">The insight engine selects the metrics with the most meaningful spread across sites; figures are drawn from the live ledger.</p>
      </section>

      {/* Normalised comparison */}
      <section>
        <h2 className="mb-2 text-meta text-faint">Sites compared on like-for-like metrics</h2>
        <div className="overflow-x-auto rounded-card border border-line bg-card">
          <table className="w-full min-w-[34rem] text-sm">
            <thead>
              <tr className="border-b border-line bg-subtle/60 text-left text-faint">
                <th className="px-4 py-2 text-meta font-medium">Site</th>
                <th className="px-4 py-2 text-right text-meta font-medium">Budget</th>
                <th className="px-4 py-2 text-right text-meta font-medium">Spend pace</th>
                <th className="px-4 py-2 text-right text-meta font-medium">Evidence</th>
                <th className="px-4 py-2 text-right text-meta font-medium">Drift</th>
                <th className="px-4 py-2 text-meta font-medium">Signal</th>
                <th className="px-4 py-2 text-right text-meta font-medium">
                  <span className="sr-only">Trace source</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={r.projectId}
                  onClick={() => openTrace({ kind: "project", id: r.projectId }, { from: "Comparison" })}
                  className="focus-ring cursor-pointer border-b border-hairline last:border-0 hover:bg-subtle/60"
                >
                  <td className="px-4 py-2">
                    <span className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${VIZ_SERIES[idx % VIZ_SERIES.length]}`} aria-hidden />
                      <span className="font-medium text-ink">{r.name}</span>
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right text-mute data">{money(r.budgeted)}</td>
                  <td className="px-4 py-2 text-right text-ink data">{formatPct(r.spentPct)}</td>
                  <td className={`px-4 py-2 text-right data ${r.evidencePct < 0.8 ? "text-warn" : "text-ink"}`}>{formatPct(r.evidencePct)}</td>
                  <td className={`px-4 py-2 text-right data ${r.driftDays > 5 ? "text-bad" : r.driftDays > 0 ? "text-warn" : "text-ok"}`}>{r.driftDays > 0 ? `+${r.driftDays}d` : `${r.driftDays}d`}</td>
                  <td className="px-4 py-2">{r.outlier ? <Pill variant="danger">Outlier</Pill> : <Pill variant="neutral">In line</Pill>}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Trace source for ${r.name}`}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        openTrace({ kind: "project", id: r.projectId }, { from: "Comparison" });
                      }}
                    >
                      <GitFork className="h-4 w-4" /> Trace source
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <NarrativeCard
            variant="briefing"
            title="Why normalise"
            body="Raw totals hide where the money is going wrong on huge programmes. Putting every site on the same unit metrics (spend pace, evidence coverage and schedule drift) surfaces the few outliers worth a closer look. Click any row to trace it to source."
          />
        </div>
      </section>
    </div>
  );
}
